/**
 * Recuperation de compte depuis un NOUVEL appareil (aucun coffre local) via
 * la cle de recuperation publiee sur le serveur souverain
 * (`docs/ARCHITECTURE.md` §4, `apps/server` README).
 *
 * Ordre volontaire :
 *  1. le serveur fournit la `vaultKey` scellee sous la cle de recuperation ;
 *  2. la cle saisie est verifiee ET le nouveau mot de passe scelle LOCALEMENT
 *     (`resetMasterPasswordWithRecoveryKey`) -- une cle fausse echoue ici,
 *     avant toute ecriture serveur ;
 *  3. seulement ensuite le serveur recoit preuve/blob/parametres (jamais la
 *     cle de recuperation, jamais le mot de passe maitre, jamais la `vaultKey`) ;
 *  4. deverrouillage local avec le nouveau mot de passe (verifie l'aller-retour),
 *     persistance de l'enveloppe, puis rapatriement du ciphertext du coffre.
 */
import {
  DecryptionFailedError,
  InvalidParameterError,
  deriveAccountKeysAsync,
  destroyAccountKeys,
  resetMasterPasswordWithRecoveryKey,
  unlockVaultAsync,
  unprotectVaultKey,
  type KdfParams,
  type SecretKey,
} from '@klavox/crypto-core';
import { authenticateVaultRecords } from '@klavox/vault-model';
import type { VaultRepository } from './repository.js';
import { syncAttachments } from './attachment-sync.js';
import type { BreachCheckOptions } from './breach-check.js';
import {
  ExposedMasterPasswordError,
  UnconfirmedWeakMasterPasswordError,
  WeakMasterPasswordError,
  assertNewMasterPasswordAccepted,
  assertNewMasterPasswordNotExposed,
} from './master-password-gate.js';
import {
  KlavoxApiError,
  fetchServerPrelogin,
  fetchServerRecoveryInfo,
  pullSync,
  resetAccountWithRecoveryKey,
  type AuthenticatedSession,
} from './sync-client.js';
import type { AccountEnvelope, RemoteSession } from './types.js';

export interface RecoverAccountInput {
  readonly repository: VaultRepository;
  readonly baseUrl: string;
  readonly email: string;
  readonly recoveryKeyDisplay: string;
  readonly newMasterPassword: string;
  /** Consentement explicite a un nouveau mot de passe faible (`master-password-gate.ts`). */
  readonly acceptWeak?: boolean;
  /** Verification du nouveau mot de passe dans les fuites connues ; absente = non demandee, rien ne part. */
  readonly breachCheck?: BreachCheckOptions | null;
  /** Consentement explicite a un nouveau mot de passe present dans des fuites connues. */
  readonly acceptExposed?: boolean;
  /** Parametres KDF du nouveau mot de passe (defaut crypto-core). */
  readonly newKdfParams?: KdfParams;
}

export interface RecoveredAccount {
  readonly envelope: AccountEnvelope;
  /** `vaultKey` deverrouillee : l'appelant la confie a un `VaultStore` (qui la detruit au verrouillage). */
  readonly vaultKey: SecretKey;
  readonly remoteSession: RemoteSession;
  readonly itemCount: number;
  readonly folderCount: number;
}

export class LocalVaultAlreadyExistsError extends Error {
  constructor() {
    super('un coffre existe deja sur cet appareil -- deverrouillez-le ou utilisez la recuperation locale');
    this.name = 'LocalVaultAlreadyExistsError';
  }
}

/** Jamais d'ecrasement d'un coffre local existant (ses entrees seraient orphelines). */
async function assertNoLocalVault(repository: VaultRepository): Promise<void> {
  if ((await repository.loadAccountEnvelope()) !== null) throw new LocalVaultAlreadyExistsError();
}

export async function recoverAccountOnNewDevice(input: RecoverAccountInput): Promise<RecoveredAccount> {
  await assertNoLocalVault(input.repository);

  const baseUrl = input.baseUrl.trim();
  const email = input.email.trim();
  // Avant tout appel reseau : un mot de passe faible non consenti ne fait rien partir.
  assertNewMasterPasswordAccepted(input.newMasterPassword, email, input.acceptWeak === true);
  await assertNewMasterPasswordNotExposed(input.newMasterPassword, input.breachCheck ?? null, input.acceptExposed === true);
  const info = await fetchServerRecoveryInfo(baseUrl, email);

  const reset = resetMasterPasswordWithRecoveryKey({
    recoveryKeyDisplay: input.recoveryKeyDisplay,
    recoveryProtectedSymmetricKey: info.recoveryProtectedSymmetricKey,
    email,
    newMasterPassword: input.newMasterPassword,
    ...(input.newKdfParams !== undefined ? { newKdfParams: input.newKdfParams } : {}),
  });

  const session = await resetAccountWithRecoveryKey(baseUrl, {
    email: reset.email,
    recoveryAuthProofBase64: reset.recoveryAuthProofBase64,
    newMasterPasswordHashBase64: reset.masterPasswordHashBase64,
    newProtectedSymmetricKey: reset.protectedSymmetricKey,
    newKdfParams: reset.kdfParams,
  });

  const unlocked = await unlockVaultAsync({
    masterPassword: input.newMasterPassword,
    email: reset.email,
    kdfParams: reset.kdfParams,
    protectedSymmetricKey: reset.protectedSymmetricKey,
  });

  try {
    const envelope: AccountEnvelope = {
      email: reset.email,
      kdfParams: reset.kdfParams,
      protectedSymmetricKey: reset.protectedSymmetricKey,
      // Inchangee par la reinitialisation : la meme cle de recuperation reste valable.
      recoveryProtectedSymmetricKey: info.recoveryProtectedSymmetricKey,
    };
    // Persistee avant le rapatriement : si la synchro echoue, l'appareil reste
    // deverrouillable avec le nouveau mot de passe et la synchro rejouable.
    await input.repository.saveAccountEnvelope(envelope);
    return await pullIntoNewDevice(input.repository, baseUrl, session, envelope, unlocked.vaultKey);
  } catch (cause) {
    unlocked.vaultKey.destroy();
    throw cause;
  }
}

/** Rapatrie le ciphertext du compte sur l'appareil ; seuls les enregistrements authentiques sous `vaultKey` entrent. */
async function pullIntoNewDevice(
  repository: VaultRepository,
  baseUrl: string,
  session: AuthenticatedSession,
  envelope: AccountEnvelope,
  vaultKey: SecretKey,
): Promise<RecoveredAccount> {
  const pulled = await pullSync(baseUrl, session.sessionToken, 0);
  // Serveur non digne de confiance : seuls les enregistrements authentiques sont rapatries.
  const authentic = authenticateVaultRecords({ items: pulled.items, folders: pulled.folders, vaultKey });
  await repository.putItems(authentic.items);
  await repository.putFolders(authentic.folders);
  await syncAttachments({
    baseUrl,
    sessionToken: session.sessionToken,
    repository,
    remote: pulled.attachments ?? [],
    authenticate: (attachments) => authenticateVaultRecords({ attachments, vaultKey }).attachments,
  });
  return {
    envelope,
    vaultKey,
    remoteSession: {
      baseUrl,
      sessionToken: session.sessionToken,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt,
      lastSyncedAt: pulled.serverTime,
    },
    itemCount: authentic.items.filter((item) => item.deletedAt === null).length,
    folderCount: authentic.folders.filter((folder) => folder.deletedAt === null).length,
  };
}

export interface SignInOnNewDeviceInput {
  readonly repository: VaultRepository;
  readonly baseUrl: string;
  readonly email: string;
  readonly masterPassword: string;
  /** Ouverture de session, second facteur compris (l'UI branche la ceremonie WebAuthn ici). */
  readonly login: (baseUrl: string, email: string, masterPasswordHashBase64: string) => Promise<AuthenticatedSession>;
  /** Parametres KDF du compte (defaut : ceux annonces par `GET /v1/accounts/prelogin`). */
  readonly kdfParams?: KdfParams;
}

/**
 * Ouvre un compte EXISTANT sur un appareil vierge avec le mot de passe maitre
 * (changement de telephone) : c'est ce qui rend les graines TOTP, passkeys et
 * mots de passe du coffre a leur titulaire sans qu'aucun secret en clair n'ait
 * transite. Une seule derivation Argon2id : la preuve part au serveur, la
 * `stretchedMasterKey` ouvre LOCALEMENT l'enveloppe qu'il renvoie.
 *
 * Les parametres KDF viennent du prelogin (un compte dont le mot de passe a
 * ete change peut ne plus avoir les defauts). Un serveur hostile qui les
 * affaiblirait pour obtenir une preuve moins couteuse a casser est arrete par
 * le plancher de `deriveAccountKeysAsync` (`assertKdfParams`), AVANT toute
 * derivation et donc avant que la moindre preuve ne parte. L'enveloppe persistee
 * garde les parametres REELLEMENT appliques. Une enveloppe forgee echoue a
 * `unprotectVaultKey` (AEAD), avant toute ecriture locale.
 */
export async function signInOnNewDevice(input: SignInOnNewDeviceInput): Promise<RecoveredAccount> {
  await assertNoLocalVault(input.repository);
  const baseUrl = input.baseUrl.trim();
  const params = input.kdfParams ?? (await fetchServerPrelogin(baseUrl, input.email)).kdfParams;
  const keys = await deriveAccountKeysAsync({ masterPassword: input.masterPassword, email: input.email, params });
  let vaultKey: SecretKey;
  let session: AuthenticatedSession;
  try {
    session = await input.login(baseUrl, keys.email, keys.masterPasswordHashBase64);
    vaultKey = unprotectVaultKey({ protectedKey: session.protectedSymmetricKey, stretchedMasterKey: keys.stretchedMasterKey });
  } finally {
    destroyAccountKeys(keys);
  }
  try {
    const envelope: AccountEnvelope = {
      email: keys.email,
      kdfParams: keys.kdfParams,
      protectedSymmetricKey: session.protectedSymmetricKey,
    };
    await input.repository.saveAccountEnvelope(envelope);
    return await pullIntoNewDevice(input.repository, baseUrl, session, envelope, vaultKey);
  } catch (cause) {
    vaultKey.destroy();
    throw cause;
  }
}

/** Message utilisateur sans detail technique ni oracle (existence du compte non revelee). */
export function describeRecoveryError(cause: unknown): string {
  if (cause instanceof LocalVaultAlreadyExistsError) return cause.message;
  if (cause instanceof WeakMasterPasswordError || cause instanceof UnconfirmedWeakMasterPasswordError) return cause.message;
  if (cause instanceof ExposedMasterPasswordError) return cause.message;
  if (cause instanceof DecryptionFailedError) return 'cle de recuperation incorrecte';
  if (cause instanceof InvalidParameterError) return 'cle de recuperation ou e-mail mal forme';
  if (cause instanceof KlavoxApiError) {
    if (cause.code === 'recovery_unavailable') return 'recuperation indisponible pour ce compte sur ce serveur';
    if (cause.code === 'network_error') return 'serveur injoignable -- verifiez l’URL et votre connexion';
    if (cause.code === 'invalid_request') return 'requete refusee par le serveur';
  }
  return 'echec de la recuperation';
}
